// UserDashboard.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { 
  FaUsers, 
  FaUserCheck, 
  FaUserPlus, 
  FaUserClock,
  FaMale, 
  FaFemale, 
  FaTransgender,
  FaChartLine,
  FaChartBar,
  FaChartPie,
  FaCalendarAlt,
  FaGlobe,
  FaDesktop,
  FaMobile,
  FaMapMarkerAlt,
  FaClock,
  FaEnvelope,
  FaCheckCircle,
  FaTimesCircle,
  FaSort,
  FaFilter,
  FaSync,
  FaExclamationTriangle
} from 'react-icons/fa';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { 
  getAllUsers, 
  getUserStatistics, 
  getUserGrowthData,
  UserData
} from '../../../../firebase/usersFirestoreService';
import { cache } from '../../../../utils/cacheManager';
import { useAuth } from '../../../../contexts/AuthContext';

// Cache configuration
const CACHE_VERSION = 'v1.0';
const CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes
const BACKGROUND_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Cache keys generator with versioning
const createCacheKey = (prefix: string, userId: string, role?: string): string => {
  return `dashboard_${prefix}_${userId}_${role || 'user'}_${CACHE_VERSION}`;
};

// Types for state
interface StatisticsState {
  totalUsers: number;
  activeToday: number;
  activeThisWeek: number;
  activeThisMonth: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  maleCount: number;
  femaleCount: number;
  otherGenderCount: number;
  verifiedCount: number;
  topBrowsers: Array<{ browser: string; count: number }>;
  topDevices: Array<{ device: string; count: number }>;
  topLocations: Array<{ location: string; count: number }>;
  peakHours: Array<{ hour: number; count: number }>;
}

interface GrowthState {
  daily: any[];
  weekly: any[];
  monthly: any[];
  yearly: any[];
}

interface CacheMetrics {
  hits: number;
  misses: number;
  errors: number;
  lastRefresh: number | null;
}

const UserDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [statistics, setStatistics] = useState<StatisticsState>({
    totalUsers: 0,
    activeToday: 0,
    activeThisWeek: 0,
    activeThisMonth: 0,
    newUsersToday: 0,
    newUsersThisWeek: 0,
    newUsersThisMonth: 0,
    maleCount: 0,
    femaleCount: 0,
    otherGenderCount: 0,
    verifiedCount: 0,
    topBrowsers: [] as Array<{ browser: string; count: number }>,
    topDevices: [] as Array<{ device: string; count: number }>,
    topLocations: [] as Array<{ location: string; count: number }>,
    peakHours: [] as Array<{ hour: number; count: number }>
  });

  const [growthData, setGrowthData] = useState<GrowthState>({
    daily: [] as any[],
    weekly: [] as any[],
    monthly: [] as any[],
    yearly: [] as any[]
  });
  
  const [activePeriod, setActivePeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
  const [sortField, setSortField] = useState<'createdAt' | 'lastLoginAt' | 'email'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [cacheMetrics, setCacheMetrics] = useState<CacheMetrics>({
    hits: 0,
    misses: 0,
    errors: 0,
    lastRefresh: null
  });
  const [error, setError] = useState<string | null>(null);
  
  const { currentUser } = useAuth();

  // Use centralized cache (service layer handles caching) via `cache` singleton

  // Stale-while-revalidate pattern: Load cached data first, then fetch fresh
  const fetchUserData = async (background = false) => {
    if (!currentUser) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }
    
    const userRole = currentUser.email?.includes('admin') ? 'admin' : 'user'; // Adjust based on your auth system
    const userCacheKey = createCacheKey('users', currentUser.uid, userRole);
    const statsCacheKey = createCacheKey('stats', currentUser.uid, userRole);
    const growthCacheKey = createCacheKey('growth', currentUser.uid, userRole);
    
    if (!background) {
      setLoading(true);
      setError(null);
    } else {
      setBackgroundLoading(true);
    }
    
    try {
      // Let service-layer caching handle cached responses; proceed to fetch
      // Phase 2: Fetch fresh data from Firestore
      console.log(`${background ? 'Background' : 'Initial'} fetch started...`);
      
      const [statsResult, dailyResult, weeklyResult, monthlyResult, yearlyResult, usersResult] = await Promise.allSettled([
        getUserStatistics(),
        getUserGrowthData('daily'),
        getUserGrowthData('weekly'),
        getUserGrowthData('monthly'),
        getUserGrowthData('yearly'),
        getAllUsers()
      ]);
      
      // Process statistics (service is cached)
      if (statsResult.status === 'fulfilled' && statsResult.value.success) {
        setStatistics(statsResult.value.data);
      } else if (statsResult.status === 'rejected') {
        console.error('Failed to fetch statistics:', statsResult.reason);
      }
      
      // Process growth data
      const freshGrowthData = {
        daily: dailyResult.status === 'fulfilled' && dailyResult.value.success ? dailyResult.value.data : [],
        weekly: weeklyResult.status === 'fulfilled' && weeklyResult.value.success ? weeklyResult.value.data : [],
        monthly: monthlyResult.status === 'fulfilled' && monthlyResult.value.success ? monthlyResult.value.data : [],
        yearly: yearlyResult.status === 'fulfilled' && yearlyResult.value.success ? yearlyResult.value.data : []
      };
      
      setGrowthData(freshGrowthData);
      
      // Process users
      if (usersResult.status === 'fulfilled' && usersResult.value.success) {
        setUsers(usersResult.value.data);
      } else if (usersResult.status === 'rejected') {
        console.error('Failed to fetch users:', usersResult.reason);
        setError(usersResult.reason.message || 'Failed to load users');
      }
      
      setCacheMetrics(prev => ({ ...prev, lastRefresh: Date.now() }));
      
    } catch (error: any) {
      console.error('Error in fetchUserData:', error);
      setError(error.message || 'An unexpected error occurred');
      
      // If fetch fails, service-layer caching may still serve stale results on next attempt
    } finally {
      if (!background) {
        setLoading(false);
      }
      setBackgroundLoading(false);
    }
  };

  // Initial fetch - wait for currentUser
  useEffect(() => {
    if (currentUser) {
      console.log('User authenticated, fetching data...');
      fetchUserData();
    } else {
      console.log('Waiting for authentication...');
      setLoading(true);
    }
  }, [currentUser]);

  // Periodic background refresh
  useEffect(() => {
    if (!currentUser) return;
    
    const interval = setInterval(() => {
      console.log('🔄 Background cache refresh');
      fetchUserData(true);
    }, BACKGROUND_REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [currentUser]);

  // Cache invalidation on auth changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_changed' || e.key === 'user_logged_out') {
        // Refresh dashboard data when auth changes
        if (currentUser) fetchUserData();
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh in background when user returns to tab
        fetchUserData(true);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Handle manual refresh
  const handleRefresh = () => {
    if (currentUser) {
      try {
        cache.invalidate('users.all');
        cache.invalidate('users.all.includeAdmins');
        cache.invalidate('users.stats');
        cache.invalidatePattern(/^users\.growth\./);
      } catch (e) {}
      fetchUserData();
    }
  };

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(term) ||
        user.displayName?.toLowerCase().includes(term) ||
        `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(term) ||
        user.phone?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'createdAt':
          aValue = a.createdAt instanceof Date ? a.createdAt : a.createdAt?.toDate?.();
          bValue = b.createdAt instanceof Date ? b.createdAt : b.createdAt?.toDate?.();
          break;
        case 'lastLoginAt':
          aValue = a.lastLoginAt instanceof Date ? a.lastLoginAt : a.lastLoginAt?.toDate?.();
          bValue = b.lastLoginAt instanceof Date ? b.lastLoginAt : b.lastLoginAt?.toDate?.();
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        default:
          return 0;
      }
      
      // Handle null/undefined values
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [users, searchTerm, sortField, sortDirection]);

  // Handle sort
  const handleSort = (field: 'createdAt' | 'lastLoginAt' | 'email') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Format date for display
  const formatDate = (date: Date | Timestamp | undefined) => {
    if (!date) return 'Never';
    const d = date instanceof Date ? date : date?.toDate?.();
    if (!d) return 'Invalid date';
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    }) + ' ' + d.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Calculate time ago
  const timeAgo = (date: Date | Timestamp | undefined) => {
    if (!date) return 'Never';
    const d = date instanceof Date ? date : date?.toDate?.();
    if (!d) return '';
    
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 2592000) return `${Math.floor(diffSec / 86400)}d ago`;
    return `${Math.floor(diffSec / 2592000)}mo ago`;
  };

  // Calculate percentages
  const malePercentage = statistics.totalUsers > 0 
    ? ((statistics.maleCount / statistics.totalUsers) * 100).toFixed(1)
    : '0.0';
  
  const femalePercentage = statistics.totalUsers > 0 
    ? ((statistics.femaleCount / statistics.totalUsers) * 100).toFixed(1)
    : '0.0';
  
  const otherPercentage = statistics.totalUsers > 0 
    ? ((statistics.otherGenderCount / statistics.totalUsers) * 100).toFixed(1)
    : '0.0';

  // Chart data
  const genderData = [
    { name: 'Male', value: statistics.maleCount, color: '#3b82f6' },
    { name: 'Female', value: statistics.femaleCount, color: '#ec4899' },
    { name: 'Other', value: statistics.otherGenderCount, color: '#8b5cf6' }
  ];

  const browserData = statistics.topBrowsers.map(browser => ({
    name: browser.browser,
    count: browser.count,
    color: '#10b981'
  }));

  const deviceData = statistics.topDevices.map(device => ({
    name: device.device,
    count: device.count,
    color: '#f59e0b'
  }));

  const peakHourData = statistics.peakHours.map(hour => ({
    hour: `${hour.hour}:00`,
    count: hour.count,
    color: '#ef4444'
  }));

  // If the service didn't provide peak hours, derive them from users' lastLoginAt timestamps.
  const derivedPeakHourData = (() => {
    if (Array.isArray(statistics.peakHours) && statistics.peakHours.length > 0) return peakHourData;
    if (!users || users.length === 0) return [] as any[];

    const counts = new Array<number>(24).fill(0);
    users.forEach(u => {
      try {
        const d = u.lastLoginAt instanceof Date ? u.lastLoginAt : u.lastLoginAt?.toDate?.();
        if (!d) return;
        const h = d.getHours();
        if (typeof h === 'number' && h >= 0 && h < 24) counts[h]++;
      } catch (e) {
        // ignore
      }
    });

    const arr = counts.map((count, hour) => ({ hour: `${hour}:00`, count, color: '#ef4444' }));
    return arr.filter(a => a.count > 0);
  })();

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Cache metrics display
  const renderCacheMetrics = () => (
    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
      <div className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-green-500"></span>
        <span>Cache hits: {cacheMetrics.hits}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
        <span>Misses: {cacheMetrics.misses}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-red-500"></span>
        <span>Errors: {cacheMetrics.errors}</span>
      </div>
      {cacheMetrics.lastRefresh && (
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
          <span>Last refresh: {new Date(cacheMetrics.lastRefresh).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      )}
      {backgroundLoading && (
        <span className="text-blue-600 animate-pulse">Refreshing...</span>
      )}
    </div>
  );

  // Loading skeleton
  if (loading && users.length === 0) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-48 bg-gray-100 rounded mt-2 animate-pulse"></div>
          </div>
          <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 p-6 rounded-xl animate-pulse h-32"></div>
          ))}
        </div>
        
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading user dashboard...</p>
            <p className="text-sm text-gray-400">Fetching data from Firestore</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management Dashboard</h1>
          <p className="text-gray-600 mt-1">Track and manage registered users</p>
          {renderCacheMetrics()}
        </div>
        <div className="flex items-center gap-3">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg border border-red-200">
              <FaExclamationTriangle />
              <span className="text-sm">Data may be outdated</span>
            </div>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaSync className={loading ? 'animate-spin' : ''} />
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {error} - Showing cached data. <button onClick={handleRefresh} className="font-medium underline">Try again</button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.totalUsers.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Excluding admin users</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FaUsers className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Today</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.activeToday.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                {statistics.totalUsers > 0 
                  ? `${((statistics.activeToday / statistics.totalUsers) * 100).toFixed(1)}% of total`
                  : '0% of total'
                }
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FaUserCheck className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New This Month</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.newUsersThisMonth.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                +{statistics.newUsersToday} today
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FaUserPlus className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Verified Users</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.verifiedCount.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                {statistics.totalUsers > 0 
                  ? `${((statistics.verifiedCount / statistics.totalUsers) * 100).toFixed(1)}% verified`
                  : '0% verified'
                }
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <FaCheckCircle className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Gender Distribution Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FaMale className="h-5 w-5 text-blue-500" />
                <p className="text-sm font-medium text-gray-700">Male Users</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">{statistics.maleCount.toLocaleString()}</p>
              <p className="text-sm text-gray-500">{malePercentage}%</p>
            </div>
            <div className="w-20 h-20">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[{ value: statistics.maleCount }, { value: Math.max(statistics.totalUsers - statistics.maleCount, 0) }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={40}
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    <Cell fill="#3b82f6" />
                    <Cell fill="#f3f4f6" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FaFemale className="h-5 w-5 text-pink-500" />
                <p className="text-sm font-medium text-gray-700">Female Users</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">{statistics.femaleCount.toLocaleString()}</p>
              <p className="text-sm text-gray-500">{femalePercentage}%</p>
            </div>
            <div className="w-20 h-20">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[{ value: statistics.femaleCount }, { value: Math.max(statistics.totalUsers - statistics.femaleCount, 0) }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={40}
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    <Cell fill="#ec4899" />
                    <Cell fill="#f3f4f6" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FaTransgender className="h-5 w-5 text-purple-500" />
                <p className="text-sm font-medium text-gray-700">Other/Not Specified</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">{statistics.otherGenderCount.toLocaleString()}</p>
              <p className="text-sm text-gray-500">{otherPercentage}%</p>
            </div>
            <div className="w-20 h-20">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[{ value: statistics.otherGenderCount }, { value: Math.max(statistics.totalUsers - statistics.otherGenderCount, 0) }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={40}
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    <Cell fill="#8b5cf6" />
                    <Cell fill="#f3f4f6" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">User Growth</h3>
            <div className="flex space-x-2">
              {(['daily', 'weekly', 'monthly', 'yearly'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setActivePeriod(period)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    activePeriod === period
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64 min-h-[250px]">
            {growthData[activePeriod].length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={growthData[activePeriod]}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#3b82f6"
                    fillOpacity={1}
                    fill="url(#colorGrowth)"
                    name="Total Users"
                  />
                  <Area
                    type="monotone"
                    dataKey="newUsers"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.2}
                    name="New Users"
                  />
                  <Area
                    type="monotone"
                    dataKey="activeUsers"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.2}
                    name="Active Users"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No growth data available
              </div>
            )}
          </div>
        </div>

        {/* Browser/Device Distribution */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Browser & Device Usage</h3>
            <div className="flex space-x-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm">Browsers</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-sm">Devices</span>
              </div>
            </div>
          </div>
          <div className="h-64 min-h-[250px]">
            {browserData.length > 0 || deviceData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...browserData, ...deviceData]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="count" name="Usage Count">
                    {[...browserData, ...deviceData].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No usage data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Peak Hours Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Peak Activity Hours</h3>
            <p className="text-sm text-gray-600">User activity by hour of day</p>
          </div>
          <FaClock className="h-5 w-5 text-gray-400" />
        </div>
        <div className="h-64 min-h-[250px]">
          {peakHourData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={peakHourData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Activity Count"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              No activity data available
            </div>
          )}
        </div>
      </div>

      {/* Users List Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Registered Users</h3>
              <p className="text-sm text-gray-600">
                Showing {Math.min(filteredUsers.length, 20)} of {filteredUsers.length} users
              </p>
            </div>
            <div className="flex gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                />
                <FaFilter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    Email
                    <FaSort className="h-3 w-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gender
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1">
                    Joined
                    <FaSort className="h-3 w-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('lastLoginAt')}
                >
                  <div className="flex items-center gap-1">
                    Last Active
                    <FaSort className="h-3 w-3" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.slice(0, 20).map((user) => (
                <tr key={user.uid} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.profilePhoto ? (
                          <img 
                            src={user.profilePhoto} 
                            alt={user.displayName || user.email}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="font-semibold text-blue-600">
                              {(user.firstName?.[0] || user.email[0] || 'U').toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Name'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.phone || 'No phone'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    <div className="text-xs">
                      {user.emailVerified ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <FaCheckCircle className="h-3 w-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <FaTimesCircle className="h-3 w-3" />
                          Unverified
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {user.gender === 'male' && <FaMale className="h-4 w-4 text-blue-500" />}
                      {user.gender === 'female' && <FaFemale className="h-4 w-4 text-pink-500" />}
                      {!['male', 'female'].includes(user.gender || '') && <FaTransgender className="h-4 w-4 text-purple-500" />}
                      <span className="text-sm text-gray-900 capitalize">
                        {user.gender?.replace('-', ' ') || 'Not specified'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      user.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{timeAgo(user.lastLoginAt)}</div>
                    <div className="text-xs text-gray-500">
                      {formatDate(user.lastLoginAt)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FaUsers className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-lg font-medium">No users found</p>
            <p className="text-sm mt-1">
              {searchTerm ? 'Try a different search term' : 'No users in the database'}
            </p>
          </div>
        )}

        {filteredUsers.length > 20 && (
          <div className="p-4 border-t border-gray-200 text-center">
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors">
              Load More Users ({filteredUsers.length - 20} remaining)
            </button>
          </div>
        )}
      </div>

      {/* Analytics Summary */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Analytics Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Top Browsers</h4>
            <div className="space-y-2">
              {statistics.topBrowsers.slice(0, 3).map((browser, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaGlobe className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{browser.browser}</span>
                  </div>
                  <span className="text-sm font-medium">{browser.count}</span>
                </div>
              ))}
              {statistics.topBrowsers.length === 0 && (
                <p className="text-sm text-gray-400">No browser data</p>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Top Devices</h4>
            <div className="space-y-2">
              {statistics.topDevices.slice(0, 3).map((device, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {device.device.includes('Mobile') || device.device.includes('mobile') ? (
                      <FaMobile className="h-4 w-4 text-gray-400" />
                    ) : (
                      <FaDesktop className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm">{device.device}</span>
                  </div>
                  <span className="text-sm font-medium">{device.count}</span>
                </div>
              ))}
              {statistics.topDevices.length === 0 && (
                <p className="text-sm text-gray-400">No device data</p>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Top Locations</h4>
            <div className="space-y-2">
              {statistics.topLocations.slice(0, 3).map((location, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{location.location}</span>
                  </div>
                  <span className="text-sm font-medium">{location.count}</span>
                </div>
              ))}
              {statistics.topLocations.length === 0 && (
                <p className="text-sm text-gray-400">No location data</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer note */}
      <div className="text-center text-xs text-gray-400 pt-4 border-t border-gray-100">
        <p>Dashboard data is cached for performance. Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
        <p className="mt-1">Cache version: {CACHE_VERSION} • Next refresh in {Math.round((BACKGROUND_REFRESH_INTERVAL / 60000))} minutes</p>
      </div>
    </div>
  );
};

export default UserDashboard;